import { FC, ChangeEvent, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot,
} from 'react-beautiful-dnd';
import intl from 'react-intl-universal';
import {
  tailwindOverride,
  Checkbox,
  Typography,
  Icon,
  decimalHoursToTimeString,
} from '@getsynapse/design-system';
import { TaskWithAssignees } from 'types/store/projectTasksList';
import { PATHS, TASK_STATUS } from 'utils/constants';
import { RootState } from 'state/store';
import {
  getIsProjectClosed,
  getIsProjectCanceled,
} from 'state/Project/projectSlice';
import { selectTaskTypeByCode } from 'state/TasksList/allTasks/allTasksSlice';
import { selectIsTaskSelected } from 'state/ProjectTasksList/export/exportSlice';
import {
  getDueAndActualDateColumn,
  getStatusColumn,
} from 'Pages/ProjectsListPage/helpers/tableColumnsValues';
import AssigneesColumn from 'Pages/TasksListPage/components/v2/TasksListTable/TaskRow/AssigneesColumn/AssigneesColumn';
import DisableColumnIcon from '../DisableColumnIcon/DisableColumnIcon';
import ActionButtonsColumn from '../ActionButtonsColumn/ActionButtonsColumn';
import DragAnDropHandler from 'assets/icons/drag-and-drop-handler.svg';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  AssigneeTableRowCell,
  HoursTableRowCell,
} from 'Atoms/EditableTableRowCell';
import { useUpdateCells } from '../hooks/useUpdateCells';
import { LinkTableRowCell } from 'Atoms/LinkTableRowCell';
import { updateTaskInList } from 'state/ProjectTasksList/projectTasksListSlice';
import { TaskListElement } from 'types/store/tasksList';
import { PilotPagesToTrack } from 'utils/customTypes';
import { StatusTableRowCell } from 'Atoms/EditableTableRowCell/StatusTableRowCell';
import { setTasksAssignees } from 'state/TasksList/allTasks/allTasksSlice';
import { TaskAssignee } from 'types/store/tasks';

interface TaskRowProps {
  index: number;
  task: TaskWithAssignees;
  openTakInSidePanel: (taskId: string) => void;
  handleClickTaskRow: (taskId: string, projectId: string) => void;
  handleDuplicateTask: (taskId: string) => void;
  handleSelectRow: (taskId: string, selected: boolean) => void;
  bgColor?: string;
  showDisabledColumn?: boolean;
  canReorderTasks?: boolean;
  refreshTasks: () => void;
}

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
});

const TaskRow: FC<TaskRowProps> = ({
  index,
  task,
  openTakInSidePanel,
  handleDuplicateTask,
  handleSelectRow,
  bgColor = 'bg-neutral-white',
  showDisabledColumn = false,
  canReorderTasks = false,
  refreshTasks,
}) => {
  const isProjectClosed = useSelector(getIsProjectClosed);
  const isProjectCanceled = useSelector(getIsProjectCanceled);
  const taskType = useSelector((state: RootState) =>
    selectTaskTypeByCode(state, task.type)
  );
  const isTaskRowSelected = useSelector((state: RootState) =>
    selectIsTaskSelected(state, task.id)
  );
  const { inlineEditingTaskAssignee, flexibleTaskTimeEntry } = useFlags();

  const [isAssigneesDropdownOpened, setIsAssigneesDropdownOpened] =
    useState<boolean>(false);

  const columnStyles = tailwindOverride(
    'group-hover:border-secondary-darker group-hover:shadow-none',
    'border-b border-t border-r-0 border-transparent',
    'px-4 py-3',
    bgColor,
    {
      'bg-secondary-lightest border-secondary-darker shadow-none':
        isTaskRowSelected,
      'shadow-table-column': !isTaskRowSelected,
    }
  );

  const stickyColumnStyles = tailwindOverride('sticky z-1', columnStyles);

  const formatDate = (dateString: string): string => {
    return dateFormatter.format(new Date(dateString));
  };

  const isOnHold = task.status === TASK_STATUS.ON_HOLD;
  const hasPermission = task.permissions?.canEdit;
  const isTaskProjectDisabled = isProjectClosed || isProjectCanceled;
  const canEditStatus =
    !task.disabled && !isTaskProjectDisabled && hasPermission;

  const canInlineEditTask = !isOnHold && !task.disabled;

  const canEdit =
    !isOnHold && !isTaskProjectDisabled && hasPermission && !task.disabled;

  const isViewOnly = !canEdit;
  const isOnlyStatusViewMode =
    !task.permissions?.canEdit || !canEditStatus || isTaskProjectDisabled;

  const dispatch = useDispatch();

  const updateTaskInListAction = (
    taskId: string,
    updatedTask: Partial<TaskListElement>
  ) => {
    dispatch(
      updateTaskInList({
        taskId,
        updatedTask,
      })
    );
  };

  const updateAssignees = (updatedAssignees: TaskAssignee[]) => {
    dispatch(setTasksAssignees(updatedAssignees));
  };

  const getActualHours = (hours?: string) => {
    if (flexibleTaskTimeEntry) {
      return decimalHoursToTimeString(parseFloat(hours || '0'));
    }

    return hours ? `${parseFloat(hours)}h` : '0h';
  };

  const {
    handleSaveEstimateHours,
    handleSaveTaskStatus,
    statuses,
    handleSaveAssignees,
    handleSearchAssignees,
    availableAssignees,
    isFetchingAvailableAssignees,
  } = useUpdateCells({
    isAssigneesDropdownOpened,
    canUpdateTask: !!canEdit,
    canUpdateStatus: canEditStatus,
    isViewOnly,
    task,
    updateTaskInList: updateTaskInListAction,
    updateAssignees,
  });

  return (
    <Draggable draggableId={task.id} index={index}>
      {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
        <tr
          ref={provided.innerRef}
          aria-label={`task-row-${task.id}`}
          className={tailwindOverride(
            'group relative',
            'text-label text-primary group',
            'hover:shadow-lifted',
            {
              bgColor: !isTaskRowSelected,
            }
          )}
          {...provided.draggableProps}
        >
          <td
            className={tailwindOverride(
              'left-0',
              'group-hover:border-secondary-darker  group-hover:shadow-none',
              'border-b border-t border-l border-r-0 border-transparent',
              'sticky z-1 pl-px pr-0.5',
              bgColor,
              {
                'border-secondary-darker': snapshot.isDragging,
                'bg-secondary-lightest border-secondary-darker shadow-first-table-cell-hover':
                  isTaskRowSelected,
                'shadow-table-column': !isTaskRowSelected,
              }
            )}
          >
            <div className='w-5 h-5'>
              <Icon
                src={DragAnDropHandler}
                className={tailwindOverride('text-xl hidden', {
                  'group-hover:block': canReorderTasks,
                  block: snapshot.isDragging,
                })}
                {...provided.dragHandleProps}
              />
            </div>
          </td>
          <td
            className={tailwindOverride(
              'w-8 left-6',
              stickyColumnStyles,
              'pl-2 pr-4',
              { 'border-secondary-darker': snapshot.isDragging }
            )}
          >
            <div
              className='w-8'
              onClick={(event: React.MouseEvent<HTMLInputElement>) => {
                event.stopPropagation();
              }}
            >
              <Checkbox
                checked={isTaskRowSelected}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  handleSelectRow(task.id, event.target.checked);
                }}
                label=''
                aria-label={`task-row-${task.id}-checkbox`}
                inputProps={{
                  className: 'mr-0 mt-0',
                }}
              />
            </div>
          </td>
          <td
            className={tailwindOverride(
              'w-12 left-20',
              stickyColumnStyles,
              'p-0',
              {
                hidden: !showDisabledColumn,
                'border-secondary-darker': snapshot.isDragging,
              }
            )}
          >
            <div
              className={tailwindOverride(
                'w-12',
                'flex items-center justify-center'
              )}
            >
              <DisableColumnIcon
                taskId={task.id}
                isTaskDisabled={task.disabled || false}
                isTaskCompleted={task.status === TASK_STATUS.COMPLETED}
              />
            </div>
          </td>
          <td
            className={tailwindOverride(stickyColumnStyles, 'w-18', {
              'left-20': !showDisabledColumn,
              'left-32': showDisabledColumn,
              'border-secondary-darker': snapshot.isDragging,
            })}
          >
            <div className='w-18'>{task.displayId}</div>
          </td>
          <td
            className={tailwindOverride(stickyColumnStyles, 'w-1/3', {
              'left-46': !showDisabledColumn,
              'left-58': showDisabledColumn,
              'border-secondary-darker': snapshot.isDragging,
            })}
          >
            <LinkTableRowCell
              value={task.name}
              to={`${PATHS.PROJECT_PAGE}/${task.projectId}/tasks/${task.id}`}
              className='w-70'
            />
          </td>
          <td
            className={tailwindOverride(columnStyles, 'w-20 p-0', {
              'border-secondary-darker': snapshot.isDragging,
            })}
          >
            <StatusTableRowCell
              value={task.status}
              customizedValue={(value) =>
                getStatusColumn(value, false, '', true, canEditStatus)
              }
              options={statuses}
              isEditable={!isOnlyStatusViewMode}
              className='w-32'
              isTaskProjectDisabled={isTaskProjectDisabled}
              canInlineEditTask={!task.disabled}
              onSave={handleSaveTaskStatus}
              canEdit={canEditStatus}
              userPilotEventOrigin={PilotPagesToTrack.PROJECT_TASK}
            />
          </td>
          <td
            className={tailwindOverride('w-44', columnStyles, {
              'border-secondary-darker': snapshot.isDragging,
              'p-0': inlineEditingTaskAssignee,
            })}
          >
            <div className='truncate w-44'>
              {inlineEditingTaskAssignee ? (
                <AssigneeTableRowCell
                  currentAssignees={task.assignees}
                  availableAssignees={availableAssignees}
                  isEditable={!isViewOnly}
                  hasPermission={hasPermission}
                  onSave={handleSaveAssignees}
                  onSearchChange={handleSearchAssignees}
                  isTaskProjectDisabled={isTaskProjectDisabled}
                  canInlineEditTask={canInlineEditTask}
                  setIsTimeToFetchData={setIsAssigneesDropdownOpened}
                  isFetchingAvailableAssignees={isFetchingAvailableAssignees}
                />
              ) : (
                <AssigneesColumn assignees={task.assignees} />
              )}
            </div>
          </td>
          <td
            className={tailwindOverride('w-31', columnStyles, {
              'border-secondary-darker': snapshot.isDragging,
            })}
          >
            <div className='w-31 truncate' title={taskType}>
              {taskType ?? intl.get('TASKS.TABLE.NO_TASK_TYPE')}
            </div>
          </td>
          <td
            className={tailwindOverride('w-38', columnStyles, {
              'border-secondary-darker': snapshot.isDragging,
            })}
          >
            <div
              className={tailwindOverride('w-38', {
                'text-neutral-dark': !task.startDate,
              })}
            >
              {task.startDate
                ? formatDate(task.startDate)
                : intl.get('TASKS.TABLE.NO_DATE')}
            </div>
          </td>
          <td
            className={tailwindOverride('w-60', columnStyles, {
              'border-secondary-darker': snapshot.isDragging,
            })}
          >
            <div className='w-60'>
              {getDueAndActualDateColumn({
                status: task.status,
                due_date: task.endDate,
                completion_date: task.completedDate,
                id: task.id,
              })}
            </div>
          </td>
          <td
            className={tailwindOverride('w-34', columnStyles, 'p-0', {
              'border-secondary-darker': snapshot.isDragging,
            })}
          >
            <div className='w-34'>
              <HoursTableRowCell
                hasPermission={hasPermission}
                value={parseFloat(task.estimateHours || '0')}
                isEditable={!isViewOnly}
                isTaskProjectDisabled={isTaskProjectDisabled}
                canInlineEditTask={canInlineEditTask}
                onSave={handleSaveEstimateHours}
                className='px-4 py-3'
                userPilotEventOrigin={PilotPagesToTrack.PROJECT_TASK}
              />
            </div>
          </td>
          <td
            className={tailwindOverride('w-34', columnStyles, {
              'border-secondary-darker': snapshot.isDragging,
            })}
          >
            <div className='w-34'>
              <Typography variant='label' className='text-primary'>
                {getActualHours(task.actualHours)}
              </Typography>
            </div>
          </td>
          <td
            className={tailwindOverride(
              'w-12 right-0 border-r',
              stickyColumnStyles,
              { 'border-secondary-darker': snapshot.isDragging }
            )}
          >
            <div
              className='w-18'
              onClick={(event: React.MouseEvent<HTMLInputElement>) => {
                event.stopPropagation();
              }}
            >
              <div className='hidden group-hover:flex'>
                <ActionButtonsColumn
                  taskId={task.id}
                  openTakInSidePanel={openTakInSidePanel}
                  displayDuplicateTaskModal={handleDuplicateTask}
                  canDuplicateTask={hasPermission}
                  canEditTask={!isViewOnly}
                  task={task}
                  refreshTasks={refreshTasks}
                />
              </div>
            </div>
          </td>
        </tr>
      )}
    </Draggable>
  );
};

export default TaskRow;
